/** Ce que lit {@link keepLayerAcrossStyleChanges} d'une carte MapLibre. */
export interface StyleReloadHost {
  on(type: 'styledata', listener: () => void): unknown;
  off(type: 'styledata', listener: () => void): unknown;
  once(type: 'idle', listener: () => void): unknown;
  getLayer(id: string): unknown;
  getLayersOrder(): string[];
  moveLayer(id: string): unknown;
}

/**
 * Garde une couche ajoutée à la main (la couche three.js du bâtiment) visible quand le fond de carte
 * change (rue ↔ satellite) :
 * - `setStyle` appliqué par diff la garde mais empile le nouveau fond (imagerie opaque) au-dessus :
 *   elle est remise en haut de la pile, sous les seules couches de l'application `overlays` ;
 * - un style rechargé en entier la retire : `reAdd` est rappelé ; si le nouveau style n'est pas
 *   encore chargé (MapLibre refuse alors `addLayer`), on réessaie quand la carte est au repos.
 *
 * @param overlays couches de l'application dessinées par-dessus le bâtiment (trajet et points du
 *   rejeu), remontées avec lui
 * @returns une fonction qui arrête le suivi
 */
export function keepLayerAcrossStyleChanges(
  map: StyleReloadHost,
  layerId: string,
  reAdd: () => void,
  overlays: readonly string[] = []
): () => void {
  let stopped = false;
  const onStyle = () => {
    if (stopped) {
      return;
    }
    if (map.getLayer(layerId)) {
      const order = map.getLayersOrder();
      const above = order.filter(id => overlays.includes(id));
      const expected = [...order.filter(id => id !== layerId && !overlays.includes(id)), layerId, ...above];
      if (expected.some((id, i) => order[i] !== id)) {
        [layerId, ...above].forEach(id => map.moveLayer(id));
      }
      return;
    }
    try {
      reAdd();
    } catch {
      map.once('idle', onStyle);
    }
  };
  map.on('styledata', onStyle);
  return () => {
    stopped = true;
    map.off('styledata', onStyle);
  };
}
